"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ISDESubsidy = void 0;
const _2021_1 = require("./2021");
const _2022_1 = require("./2022");
const _2023_1 = require("./2023");
const _2025_1 = require("./2025");
const types_1 = require("./types");
// the current year in which subsidy can be requested
const currentYear = 2025;
/**
 * Use the methods on this class for calculating the ISDE subsidy that can be requested for a specific solution, quote / job, or package of quotes / jobs.
 * The subsidy year refers to the year in which the customer requests their subsidy.
 *
 * Requirements for subsidy calculations:
 * - Must support calculating total ISDE subsidy on a single quote / job.
 * - Must support calculating total ISDE subsidy for a set of quotes / jobs, keeping into account the subsidy rules for combining quotes / jobs.
 * - Must support determining whether subsidy can be requested for a specific Solution, when given the amount of that solution to be installed (m2 or amount of products, depending on solution).
 * - Must support an explicit subsidy amount from a product (e.g. heat pumps have an explicit subsidy amount on them to override the default heatpump subsidy per unit)
 * - Must support ISDE subsidy calculations for multiple years.
 * - Should support calculating subsidy for old quotes (e.g. subsidy was requested in a previous year).
 *
 * Regarding support for subsidy rules:
 * - Must support subsidy rules that depend on the amount of an item installed
 * - Must support subsidy rules that depend on the installation date of the installation.
 */
class ISDESubsidy {
    /** Returns true if year is a subsidy year for which we can calculate subsidies */
    static isSupported(year) {
        return types_1.SubsidyYears.includes(year);
    }
    /** Instantiate an {@link ISDESubsidyCalculator} for the specified year. */
    static calculator(year) {
        if (!ISDESubsidy.isSupported(year))
            throw new Error('unsupported subsidy year');
        const mapping = {
            2021: _2021_1.ISDESubsidy2021,
            2022: _2022_1.ISDESubsidy2022,
            2023: _2023_1.ISDESubsidy2023,
            2024: _2023_1.ISDESubsidy2023,
            2025: _2025_1.ISDESubsidy2025,
        };
        return new mapping[year]();
    }
    /**
     * Get the details about subsidy for this solution for a specific year (defaults to this year),
     * i.e. minimum and maximum amount of this solution that must be installed.
     * @see {@link ISDESubsidyCalculator.getDetailsBySolution}
     */
    static getDetailsBySolution(solution, year = currentYear) {
        return ISDESubsidy.calculator(year).getDetailsBySolution(solution);
    }
    /**
     * Returns all the solutions for which ISDE subsidy can be requested for a specific year (defaults to this year).
     * @see {@link ISDESubsidyCalculator.getSubsidizableSolutions}
     */
    static getSubsidizableSolutions(year = currentYear) {
        return ISDESubsidy.calculator(year).getSubsidizableSolutions();
    }
    /**
     * Returns true if the given set of solutions together is eligible for subsidy in a year (by default the current year),
     * assuming that the minimum required amount for each solution is installed.
     * @param solutions the solution or set of solutions to check
     * @see {@link ISDESubsidyCalculator.isSubsidizableSolutions}
     */
    static isSubsidizableSolutions(solutions, year = currentYear) {
        return ISDESubsidy.calculator(year).isSubsidizableSolutions(...solutions);
    }
    /**
     * Calculates ISDE subsidy in a given year (by default, the current year) for a single solution and the amount installed,
     * ignoring any requirements for multiple solutions to be installed and installation date.
     * Returns 0 if subsidy is not possible for this solution or the installed amount.
     *
     * Note: if you want to get a more accurate calculation of the ISDE subsidy for a quote / job, use {@link forInstallable}.
     * If you want to get a more accurate calculation for a set of quotes / jobs, use {@link forInstallables}.
     *
     * @param solution the solution
     * @param amountInstalled the amount of the solution that is being installed.
     * For the insulation solutions, this amount is area in m2, for heat pumps it is the amount of heat pumps installed (usually 1).
     * @see {@link ISDESubsidyCalculator.forSingleSolution}
     */
    static forSingleSolution(solution, amountInstalled, year = currentYear) {
        return ISDESubsidy.calculator(year).forSingleSolution(solution, amountInstalled);
    }
    /**
     * Calculates ISDE subsidy in a given year (by default, the current year) for one installable,
     * i.e., a quote or a job, augmented with an installation date when known.
     * The installable's items must have product information on it, primarily the ISDE code and optionally an override for
     * the subsidy per unit of that item.
     *
     * - If the installable has an installation date, then this date must be within {@link eligibleWithin}.
     * - The solution of this installable must be subsidisable in this year
     * - The minimum amount of subsidisable product for this solution must be met.
     * - The maximum amount of subsidisable product for this solution will not exceed the maximum that can be subsidised for this solution this year.
     *
     * @see {@link ISDESubsidyCalculator.forInstallable}
     */
    static forInstallable(installable, year = currentYear) {
        return ISDESubsidy.calculator(year).forInstallable(installable);
    }
    /**
     * Calculates the total ISDE subsidy that can be requested for a set of installables,
     * taking into account subsidy rules regarding solution combinations
     * and combining installables with the same solution.
     * Should use {@link forInstallable} to retrieve the subsidy for each individual installable.
     *
     * @see {@link ISDESubsidyCalculator.forInstallables}
     */
    static forInstallables(installables, year = currentYear) {
        return ISDESubsidy.calculator(year).forInstallables(installables);
    }
}
exports.ISDESubsidy = ISDESubsidy;
